import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, Clock, CheckCircle, XCircle, Mail, ArrowRight } from 'lucide-react';
import { API } from '../../App';

const AdminDashboard = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await API.get('/admin/clients');
      setClients(response.data);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const pendingClients = clients.filter(c => c.status === 'pending');
  const approvedClients = clients.filter(c => c.status === 'approved');
  const rejectedClients = clients.filter(c => c.status === 'rejected');

  const stats = [
    { 
      icon: Users, 
      label: 'Total Clients', 
      value: clients.length, 
      color: '#2C5530',
      link: '/admin/clients'
    },
    { 
      icon: Clock, 
      label: 'Pending Approval', 
      value: pendingClients.length, 
      color: '#BC562B',
      link: '/admin/clients?status=pending'
    },
    { 
      icon: CheckCircle, 
      label: 'Approved', 
      value: approvedClients.length, 
      color: '#2C5530',
      link: '/admin/clients?status=approved'
    },
    { 
      icon: Mail, 
      label: 'Invitations Sent', 
      value: '—', 
      color: '#737373',
      link: '/admin/clients'
    },
  ];

  return (
    <div data-testid="admin-dashboard">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#1A1A1A] mb-2">
          Admin Dashboard
        </h1>
        <p className="text-[#57534E]">
          Manage clients, approvals, and invitations.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
        {stats.map((stat, index) => (
          <Link
            key={stat.label}
            to={stat.link}
            className="bg-white border border-[#E7E5E4] p-6 hover:border-[#BC562B]/30 hover:shadow-lg transition-all duration-300 group"
            data-testid={`admin-stat-${index}`}
          >
            <div className="flex items-start justify-between mb-4">
              <stat.icon className="w-8 h-8" style={{ color: stat.color }} />
              <ArrowRight className="w-5 h-5 text-[#737373] group-hover:text-[#BC562B] group-hover:translate-x-1 transition-all" />
            </div>
            <p className="text-[#737373] text-sm mb-1">{stat.label}</p>
            <p className="font-['Playfair_Display'] text-3xl text-[#1A1A1A]">
              {loading ? '—' : stat.value}
            </p>
          </Link>
        ))}
      </div>

      {/* Pending Approvals */}
      {pendingClients.length > 0 && (
        <div className="bg-white border border-[#E7E5E4] p-6 md:p-8 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#BC562B]" />
              Pending Approvals
            </h2>
            <Link
              to="/admin/clients?status=pending"
              className="text-[#BC562B] text-sm font-semibold hover:underline"
            >
              View All
            </Link>
          </div>

          <div className="space-y-4">
            {pendingClients.slice(0, 5).map((client, index) => (
              <Link
                key={client.id}
                to={`/admin/clients/${client.id}`}
                className="flex items-center justify-between p-4 bg-[#F2F0E9] hover:bg-[#E7E5E4] transition-colors"
                data-testid={`pending-client-${index}`}
              >
                <div>
                  <p className="font-semibold text-[#1A1A1A]">{client.full_name}</p>
                  <p className="text-sm text-[#737373]">{client.email}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-[#737373]">Registered</p>
                  <p className="text-sm text-[#57534E]">
                    {new Date(client.created_at).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short'
                    })}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Link
          to="/admin/clients"
          className="bg-[#2C5530] text-white p-8 hover:bg-[#1E3A21] transition-colors"
          data-testid="quick-action-clients"
        >
          <Users className="w-8 h-8 mb-4" />
          <h3 className="font-['Playfair_Display'] text-xl mb-2">Manage Clients</h3>
          <p className="text-white/70 text-sm">
            View all clients, approve registrations, and access their data.
          </p>
        </Link>

        <Link
          to="/admin/clients"
          className="bg-[#BC562B] text-white p-8 hover:bg-[#9A4520] transition-colors"
          data-testid="quick-action-invitations"
        >
          <Mail className="w-8 h-8 mb-4" />
          <h3 className="font-['Playfair_Display'] text-xl mb-2">Send Invitation</h3>
          <p className="text-white/70 text-sm">
            Invite new clients with a unique access code for immediate approval.
          </p>
        </Link>
      </div>
    </div>
  );
};

export default AdminDashboard;
